import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { AlertCircle, EyeOff, ShoppingCart, TrendingUp, Package } from 'lucide-react';
import config from '../config';
import AnalyseIA from './AnalyseIA';
import DashboardLoadingScreen from './DashboardLoadingScreen';
import HiddenModulesModal from './HiddenModulesModal';
import RepartitionChart from './RepartitionChart';
import PrestashopPerformanceChart, { formateurMontant } from './PrestashopPerformanceChart';
import { useClientModuleVisibility } from '../hooks/useClientModuleVisibility';
import { lignesDuBloc, totauxDuBloc, blocsAnalysables } from '../utils/lignesDuDashboard';
import { getDateRangeFromPeriod } from '../utils/periodUtils';
import prestashopLogo from '../logos/prestashop.png';

const API_URL = config.API_URL;

/**
 * Le dashboard d'une boutique PrestaShop.
 *
 * ## Ce qu'il montre, et ce qu'il ne peut pas montrer
 *
 * Quatre modules : le chiffre d'affaires et ses commandes, sa décomposition, la répartition par
 * état de commande, et les meilleures ventes. ⚠️ **Il n'y a ni canal, ni page, ni appareil** :
 * une commande PrestaShop ne porte aucun UTM et la boutique ne compte pas de session. Ce n'est
 * pas une lacune de cet écran, c'est ce qu'une boutique sait.
 *
 * ## ⚠️ Chaque chargement lit la boutique
 *
 * PrestaShop n'agrège rien : la période est relue commande par commande, **sur le serveur du
 * client**. D'où trois choix qu'il ne faut pas défaire :
 *
 * - le serveur sert les trois premiers modules d'**une seule lecture** ;
 * - le top produits est chargé **après** l'écran, parce qu'il est le seul dont le coût croît
 *   avec le nombre de commandes — c'est le motif de `GA4DashboardView` pour `browsers` et
 *   `languages` ;
 * - la granularité se calcule **ici**, sans relire : le serveur rend toujours des journées.
 *
 * ## La structure est celle de GA4, pas celle de Matomo ou Piano
 *
 * La vue monte ses modules elle-même, à plat. Matomo et Piano enterrent les leurs dans leur
 * `PerformanceChart`, ce qui les rend impossibles à réordonner ou à masquer un par un.
 */
function PrestashopDashboardView({
  token,
  analyseAutorisee = true,
  selectedPeriod,
  customStartDate,
  customEndDate,
  prestashopBoutiques = [],
  filteredPrestashopBoutiques = [],
  prestashopBoutiqueId,
  setPrestashopBoutiqueId,
  prestashopGranularity = 'day',
  setPrestashopGranularity,
  userRole = 'USER',
  KPICard,
  formatNumber
}) {
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Le top produits vit à part : il se charge après l'écran, et son échec ne l'emporte pas.
  const [produits, setProduits] = useState(null);
  const [produitsChargement, setProduitsChargement] = useState(false);
  const [produitsErreur, setProduitsErreur] = useState(null);

  const [modalModulesOuverte, setModalModulesOuverte] = useState(false);

  const abandonRef = useRef(null);
  const abandonProduitsRef = useRef(null);

  const boutique = useMemo(
    () => filteredPrestashopBoutiques.find(b => b.id === prestashopBoutiqueId) || null,
    [filteredPrestashopBoutiques, prestashopBoutiqueId]
  );

  const cleVisibilite = prestashopBoutiqueId ? `prestashop_${prestashopBoutiqueId}` : 'prestashop';
  const { isModuleVisible, toggleModuleVisibility, hiddenModules } =
    useClientModuleVisibility(cleVisibilite, userRole);

  /** Une seule boutique retenue par le filtre : la choisir plutôt que demander de la choisir. */
  useEffect(() => {
    if (!prestashopBoutiqueId && filteredPrestashopBoutiques.length === 1) {
      setPrestashopBoutiqueId(filteredPrestashopBoutiques[0].id);
    }
  }, [prestashopBoutiqueId, filteredPrestashopBoutiques, setPrestashopBoutiqueId]);

  /**
   * Les bornes de la période, telles que la barre latérale les a choisies.
   *
   * ⚠️ Résolues par `getDateRangeFromPeriod`, et non par une table écrite ici. Celle qui s'y
   * trouvait parlait un **autre vocabulaire** — `'7d'`, `'30d'`, `'custom'` — que le sélecteur
   * n'émet pas : il émet `LAST_7_DAYS`, `LAST_30_DAYS`, `CUSTOM`. Aucune valeur ne
   * correspondait, le repli à trente jours s'appliquait donc toujours, et **changer de période
   * ne changeait rien à l'écran**. Rien ne cassait : les chiffres étaient justes, pour une
   * période que personne n'avait demandée.
   *
   * La fonction partagée corrige deux défauts de plus au passage, et c'est la raison de ne
   * jamais recalculer une période à la main :
   *
   * 1. **Les périodes nommées s'arrêtent hier**, comme partout ailleurs dans l'application. La
   *    journée en cours est incomplète, et la mêler à des journées pleines fausse la dernière
   *    colonne du graphique comme le panier moyen de la période.
   * 2. **Les bornes sont construites en heure locale.** `toISOString()` convient à un instant,
   *    pas à une borne de calendrier : à l'est de Greenwich, minuit local tombe la veille en
   *    UTC.
   */
  const bornes = useMemo(() => {
    if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return getDateRangeFromPeriod(selectedPeriod);
  }, [selectedPeriod, customStartDate, customEndDate]);

  const charger = useCallback(async () => {
    if (!prestashopBoutiqueId || !token) return;

    /*
     * ⚠️ Une période abandonnée ne doit rien écraser : sur une boutique lente, la réponse d'une
     * période qu'on vient de quitter arriverait après celle qu'on regarde. C'est le garde-fou
     * que seul GA4 porte parmi les trois dashboards existants.
     */
    if (abandonRef.current) abandonRef.current.abort();
    const abandon = new AbortController();
    abandonRef.current = abandon;

    setChargement(true);
    setErreur(null);
    setProduits(null);
    setProduitsErreur(null);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/dashboard/prestashop/timeline`,
        { itemId: prestashopBoutiqueId, ...bornes },
        { headers: { Authorization: `Bearer ${token}` }, signal: abandon.signal }
      );
      setDonnees(data);
    } catch (e) {
      if (axios.isCancel(e) || e.name === 'CanceledError') return;
      setDonnees(null);
      setErreur(e.response?.data?.error || 'La boutique n\'a pas pu être lue');
    } finally {
      if (abandonRef.current === abandon) setChargement(false);
    }
  }, [prestashopBoutiqueId, token, bornes]);

  useEffect(() => { charger(); }, [charger]);

  /**
   * Le top produits, après l'écran.
   *
   * ⚠️ **Son échec ne doit pas emporter la vue.** `order_details` est une ressource qu'on peut ne
   * pas cocher sur la clé du webservice, et c'est le cas sur la plupart des boutiques du parc :
   * le module affiche alors ce que la boutique a répondu, et les trois autres restent.
   */
  useEffect(() => {
    if (!donnees?.success || !prestashopBoutiqueId || !token) return;

    if (abandonProduitsRef.current) abandonProduitsRef.current.abort();
    const abandon = new AbortController();
    abandonProduitsRef.current = abandon;

    setProduitsChargement(true);
    axios.post(
      `${API_URL}/api/dashboard/prestashop/produits`,
      { itemId: prestashopBoutiqueId, ...bornes },
      { headers: { Authorization: `Bearer ${token}` }, signal: abandon.signal }
    )
      .then(({ data }) => setProduits(data))
      .catch((e) => {
        if (axios.isCancel(e) || e.name === 'CanceledError') return;
        setProduitsErreur(e.response?.data?.error || 'Les ventes par produit n\'ont pas pu être lues');
      })
      .finally(() => {
        if (abandonProduitsRef.current === abandon) setProduitsChargement(false);
      });
  }, [donnees, prestashopBoutiqueId, token, bornes]);

  /**
   * Les états, avec la mention de ceux que le périmètre écarte.
   *
   * ⚠️ **Ce bloc n'applique pas le périmètre**, délibérément : c'est lui qui montre ce que le
   * périmètre a retiré. Mais la mention manquait — la route porte `dansLePerimetre` par ligne
   * depuis l'origine, et rien ne la lisait. Un lecteur voyait donc « Annulé · 9 commandes ·
   * 692 € » sans pouvoir deviner que ces 692 € ne sont pas dans le chiffre d'affaires affiché
   * en haut de l'écran, et la somme de la colonne ne tombait jamais juste.
   */
  const etatsVentiles = useMemo(() => (donnees?.data?.etats || []).map(e => (
    e.dansLePerimetre ? e : { ...e, etat: `${e.etat} · hors CA` }
  )), [donnees]);

  const devise = donnees?.devise || boutique?.devise || 'EUR';
  const montant = useMemo(() => formateurMontant(devise), [devise]);

  const sectionStyle = {
    background: '#FAF9F5', borderRadius: '12px', padding: '24px', marginBottom: '24px'
  };

  /** La charge envoyée à l'analyse IA, construite **à l'envoi** et non à l'ouverture. */
  const construireChargeAnalyse = useCallback(() => {
    const blocs = blocsAnalysables(donnees?.data || {});
    return {
      dashboardType: 'prestashop',
      compte: boutique?.name || null,
      periode: donnees?.period || bornes,
      devise,
      // Le périmètre voyage avec les chiffres : un CA sans lui n'est comparable à rien.
      perimetre: donnees?.perimetreLibelle || null,
      etatsRetenus: donnees?.etatsRetenus || null,
      totaux: totauxDuBloc(donnees?.data?.summary || {}),
      blocs: Object.fromEntries(
        Object.entries(blocs).map(([nom, lignes]) => [nom, lignesDuBloc(lignes)])
      ),
      ...(produits?.data?.produits ? { topProduits: lignesDuBloc(produits.data.produits) } : {})
    };
  }, [donnees, produits, boutique, bornes, devise]);

  /* ── Cas 1 : chargement ──────────────────────────────────────────────────── */
  if (chargement && !donnees) {
    return (
      <DashboardLoadingScreen
        platformName="PrestaShop"
        features={[
          { icon: <ShoppingCart size={28} />, label: 'Commandes et chiffre d\'affaires' },
          { icon: <TrendingUp size={28} />, label: 'Évolution et panier moyen' },
          { icon: <Package size={28} />, label: 'Meilleures ventes' }
        ]}
      />
    );
  }

  /* ── Cas 2 : aucune boutique connectée ───────────────────────────────────── */
  if (prestashopBoutiques.length === 0) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <img src={prestashopLogo} alt="" style={{ width: '48px', height: '48px' }} />
          <h3>Aucune boutique PrestaShop connectée</h3>
          <p>Connectez une boutique pour suivre son chiffre d'affaires réel.</p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: 'connectors' }))}
            style={{
              padding: '9px 16px', borderRadius: '8px', border: 'none',
              background: '#111827', color: 'white', cursor: 'pointer', fontSize: '13px'
            }}
          >
            Ajouter un connecteur
          </button>
        </div>
      </div>
    );
  }

  /* ── Cas 2b : des boutiques, mais aucune ne passe le filtre client ou site ── */
  if (filteredPrestashopBoutiques.length === 0) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <img src={prestashopLogo} alt="" style={{ width: '48px', height: '48px' }} />
          <h3>Aucune boutique pour cette sélection</h3>
          <p>
            Des boutiques sont connectées, mais aucune n'est rattachée au client ou au site
            actuellement choisi.
          </p>
        </div>
      </div>
    );
  }

  /* ── Cas 3 : plusieurs boutiques, aucune choisie ─────────────────────────── */
  if (!prestashopBoutiqueId) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <img src={prestashopLogo} alt="" style={{ width: '48px', height: '48px' }} />
          <h3>Choisissez une boutique</h3>
          <select
            className="account-selection-dropdown"
            value=""
            onChange={(e) => setPrestashopBoutiqueId(e.target.value)}
          >
            <option value="" disabled>Boutique…</option>
            {filteredPrestashopBoutiques.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  const resume = donnees?.data?.summary || null;
  const aDesDonnees = resume && (resume.transactions > 0 || resume.totalRevenue > 0);

  return (
    <div className="prestashop-dashboard-view">
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '16px', gap: '12px', flexWrap: 'wrap'
      }}>
        {/*
          La devise est écrite **une fois**, en tête, et pas répétée dans chaque cellule : c'est
          la seule chose que cet écran sait de la boutique que les autres ignorent.
        */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={prestashopLogo} alt="" style={{ width: '28px', height: '28px' }} />
          <div>
            <div style={{ fontWeight: 600 }}>{boutique?.name || 'Boutique'}</div>
            {/*
              ⚠️ **L'en-tête nomme la règle, il n'énumère pas ce qu'elle retient.** Les états
              comptés y figuraient : six sur une boutique de démonstration, mais **vingt-trois**
              sur une vraie — « En attente de réapprovisionnement (payé) », « Expédié
              directement d'Allemagne », « Régul commande »… Trois lignes de gris qui noyaient
              la devise et le périmètre, c'est-à-dire ce qu'on vient y lire.

              Les passer au survol ne faisait que cacher la duplication : le bloc « États de
              commande » liste **déjà** chaque état avec ses commandes et son chiffre
              d'affaires, et il le fait mieux — avec les nombres, et en distinguant ce qui
              compte de ce qui est écarté. C'est là qu'un total se vérifie, pas dans une bulle
              de vingt-trois lignes.
            */}
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {devise}
              {donnees?.perimetreLibelle ? ` · ${donnees.perimetreLibelle}` : ''}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {userRole !== 'CLIENT' && hiddenModules?.length > 0 && (
            <button
              className="hidden-modules-link"
              onClick={() => setModalModulesOuverte(true)}
            >
              <EyeOff size={14} /> Modules masqués ({hiddenModules.length})
            </button>
          )}
          <AnalyseIA
            token={token}
            autorise={analyseAutorisee}
            pret={Boolean(aDesDonnees)}
            compte={boutique?.name || null}
            construireCharge={construireChargeAnalyse}
          />
        </div>
      </div>

      <HiddenModulesModal
        isOpen={modalModulesOuverte}
        onClose={() => setModalModulesOuverte(false)}
        hiddenModules={hiddenModules}
        onRestore={toggleModuleVisibility}
      />

      {erreur && (
        <div style={{
          display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '20px',
          padding: '12px 14px', borderRadius: '8px',
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '13px'
        }}>
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
          <span>{erreur}</span>
        </div>
      )}

      {/*
        ⚠️ Le bandeau de qualité. « Vides par erreur, pas par absence de ventes » — c'est la
        distinction que GA4 pose déjà, et elle vaut doublement ici : un minorant qui se présente
        comme un total fait conclure à une baisse de chiffre d'affaires.
      */}
      {donnees && (donnees.partial || donnees.noteMinorant || donnees.troncature) && (
        <div style={{
          marginBottom: '20px', padding: '12px 14px', borderRadius: '8px',
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
          fontSize: '13px', lineHeight: 1.5
        }}>
          {donnees.partial && donnees.failedBlocks?.length > 0 && (
            <div>
              {donnees.failedBlocks.join(', ')} n'a pas pu être lu : le bloc concerné est vide
              par erreur, pas par absence de données.
            </div>
          )}
          {donnees.noteMinorant && <div>{donnees.noteMinorant}</div>}
          {donnees.troncature && <div>{donnees.troncature}</div>}
        </div>
      )}

      {!aDesDonnees && !erreur && (
        <div style={sectionStyle}>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Aucune commande sur cette période, dans le périmètre retenu.
          </p>
        </div>
      )}

      {aDesDonnees && (
        <>
          {(userRole !== 'CLIENT' || isModuleVisible('prestashop_performance')) && (
            <div style={sectionStyle}>
              <PrestashopPerformanceChart
                daily={donnees.data.daily}
                summary={resume}
                devise={devise}
                granularity={prestashopGranularity}
                onGranularityChange={setPrestashopGranularity}
                KPICard={KPICard}
                userRole={userRole}
                isClientModuleVisible={isModuleVisible}
                toggleClientModuleVisibility={toggleModuleVisibility}
              />
            </div>
          )}

          {/*
            La répartition par état. ⚠️ Les états **hors périmètre** y figurent, et c'est tout
            l'intérêt du module : voir ce que « commandes payées » a écarté. Les masquer le
            rendrait redondant avec les cartes du haut.
          */}
          {(userRole !== 'CLIENT' || isModuleVisible('prestashop_etats')) && (
            <div style={sectionStyle}>
              <RepartitionChart
                lignes={etatsVentiles}
                cleDimension="etatId"
                mesurePrincipale={{ cle: 'transactions', champ: 'transactions' }}
                mesureSecondaire={{ cle: 'chiffre_affaires', champ: 'totalRevenue' }}
                cleLibelle="etat"
                titre="États de commande"
                intituleColonne="État"
                nomAuSingulier="état"
                triInitial={{ key: 'totalRevenue', direction: 'desc' }}
                formatNumber={formatNumber}
                accountId={prestashopBoutiqueId}
                userRole={userRole}
                isClientModuleVisible={isModuleVisible}
                toggleClientModuleVisibility={toggleModuleVisibility}
                componentId="prestashop_etats"
                moduleId="prestashop_etats"
              />
            </div>
          )}

          {(userRole !== 'CLIENT' || isModuleVisible('prestashop_produits')) && (
            <div style={sectionStyle}>
              {produitsErreur ? (
                <div>
                  <h3 style={{ marginTop: 0, fontSize: '16px' }}>Meilleures ventes</h3>
                  {/*
                    ⚠️ Un refus nommé, pas une liste vide. `order_details` est une ressource qu'on
                    peut ne pas cocher sur la clé, et c'est le cas sur la plupart des boutiques du
                    parc : « pas d'accès » et « aucune vente » ne se confondent pas.
                  */}
                  <p style={{ color: '#92400e', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                    {produitsErreur}
                  </p>
                </div>
              ) : (
                <RepartitionChart
                  lignes={produits?.data?.produits || []}
                  cleDimension="productId"
                  mesurePrincipale={{ cle: 'quantite', champ: 'quantity' }}
                  mesureSecondaire={{ cle: 'chiffre_affaires', champ: 'totalRevenue' }}
                  cleLibelle="productName"
                  titre="Meilleures ventes"
                  intituleColonne="Produit"
                  nomAuSingulier="produit"
                  triInitial={{ key: 'totalRevenue', direction: 'desc' }}
                  graphe="barres"
                  formatNumber={formatNumber}
                  loading={produitsChargement}
                  accountId={prestashopBoutiqueId}
                  userRole={userRole}
                  isClientModuleVisible={isModuleVisible}
                  toggleClientModuleVisibility={toggleModuleVisibility}
                  componentId="prestashop_produits"
                  moduleId="prestashop_produits"
                />
              )}
              {/*
                ⚠️ La somme des ventes par produit vaut le **CA produits**, pas le chiffre
                d'affaires : les remises de panier ne sont pas portées par les lignes. Le dire
                sous le tableau évite que deux blocs du même écran se contredisent sans
                explication.
              */}
              {produits?.data?.produits?.length > 0 && (
                <p style={{ fontSize: '11px', color: '#9ca3af', margin: '10px 0 0 0' }}>
                  Le total de ce tableau vaut le CA produits ({montant(resume.productsRevenue)}),
                  et non le chiffre d'affaires : les remises de panier ne sont pas portées par les
                  lignes de commande.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PrestashopDashboardView;
